import { useState, useEffect, useCallback } from 'react';
import { useGroupActions } from '@/hooks/useGroupActions';
import { useFriendStore } from '@/stores/useFriendStore';
import { FriendInfo, CreateGroupParams, GroupInfo } from '@/api/types';
import { hasPermission } from '@/utils/permission-user-utils';
import { preventDuplicateClick } from '@/utils/my-utils';
import BaseDialog from './BaseDialog';
import ToastUtils from '@/utils/toast-utils';
import MessageManager from '@/utils/ably-im/message-manager';

interface CreateGroupDialogProps {
  open: boolean;
  onClose: () => void;
  onCreateGroupSuccess?: (group: GroupInfo) => void;
}

/**
 * 创建群组对话框
 * 提供群组名称输入、好友选择、创建群组功能
 */
export default function CreateGroupDialog({
  open,
  onClose,
  onCreateGroupSuccess
}: CreateGroupDialogProps) {
  const [groupName, setGroupName] = useState('');
  const [selectedFriends, setSelectedFriends] = useState<FriendInfo[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const { createGroup, isLoading: creating, error: createError } = useGroupActions();
  const {
    friendList,
    friendListLoading,
    friendListError,
    getFriendList
  } = useFriendStore();

  // 检查创建群组权限
  const canCreateGroup = hasPermission('CREATE_GROUP');

  // 加载好友列表
  useEffect(() => {
    if (open && friendList.length === 0) {
      getFriendList();
    }
  }, [open, friendList.length, getFriendList]);

  // 重置状态
  useEffect(() => {
    if (open) {
      setGroupName('');
      setSelectedFriends([]);
      setSearchQuery('');
    }
  }, [open]);

  /**
   * 过滤好友列表
   */
  const filteredFriends = friendList.filter(friend =>
    friend.nickname.toLowerCase().includes(searchQuery.toLowerCase()) ||
    friend.alias?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  /**
   * 切换好友选择状态
   */
  const toggleFriendSelection = (friend: FriendInfo) => {
    setSelectedFriends(prev =>
      prev.includes(friend)
        ? prev.filter(it => it.id !== friend.id)
        : [...prev, friend]
    );
  };

  /**
   * 处理创建群组
   */
  const handleCreateGroup = useCallback(
    preventDuplicateClick(async () => {
      if (!groupName.trim()) {
        return;
      }

      if (!canCreateGroup) {
        console.warn('没有创建群组权限');
        return;
      }

      ToastUtils.showLoading(true, '创建群组中...');

      const memberIds = selectedFriends.map(friend => friend.id);
      const memberHandles = selectedFriends.map(friend => friend.handle);
      const memberNames = selectedFriends.map(friend => friend.nickname);
      const params: CreateGroupParams = {
        name: groupName.trim(),
        invitee_ids: memberIds,
        handles: memberHandles
      };

      const result = await createGroup(params);
      if (result.conversation_id) {
        ToastUtils.showToastSuccess('群组创建成功');

        MessageManager.getInstance().sendGroupCreateNotification(result.conversation_id, result.id);

        setTimeout(() => {
          MessageManager.getInstance().sendGroupInviteNotification(result.conversation_id, memberHandles, memberNames);
        }, 300);

        onClose();

        onCreateGroupSuccess?.(result);
      }

      ToastUtils.showLoading(false);
    }),
    [groupName, canCreateGroup, selectedFriends, createGroup, onClose]
  );

  /**
   * 渲染好友项
   */
  const renderFriendItem = (friend: FriendInfo) => {
    const isSelected = selectedFriends.includes(friend);

    return (
      <div
        key={friend.id}
        onClick={() => toggleFriendSelection(friend)}
        className={`flex items-center p-3 rounded-lg cursor-pointer transition-colors ${isSelected
          ? 'bg-blue-50 border-2 border-blue-200'
          : 'hover:bg-gray-50 border-2 border-transparent'
          }`}
      >
        {/* 复选框 */}
        <div className="flex-shrink-0 mr-3">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${isSelected
            ? 'bg-blue-600 border-blue-600'
            : 'border-gray-300'
            }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            )}
          </div>
        </div>

        {/* 头像 */}
        <div className="flex-shrink-0 mr-3">
          {friend.avatar ? (
            <img
              src={friend.avatar}
              alt={friend.nickname}
              className="h-10 w-10 rounded-full object-cover"
            />
          ) : (
            <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
              <span className="text-gray-600 font-medium">
                {friend.nickname?.charAt(0)?.toUpperCase() || '?'}
              </span>
            </div>
          )}
        </div>

        {/* 好友信息 */}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-900">
            {friend.alias || friend.nickname}
          </p>
          {friend.alias && friend.nickname !== friend.alias && (
            <p className="text-xs text-gray-500">{friend.nickname}</p>
          )}
        </div>

        {/* 选中状态 */}
        {isSelected && (
          <div className="flex-shrink-0">
            <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
          </div>
        )}
      </div>
    );
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title="创建群组"
      maxWidth="lg"
    >
      <div className="space-y-6">
        {/* 权限检查 */}
        {!canCreateGroup && (
          <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-yellow-800">您暂无创建群组权限</p>
          </div>
        )}

        {/* 群组名称 */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            群组名称 *
          </label>
          <input
            type="text"
            placeholder="请输入群组名称"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            disabled={!canCreateGroup}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            maxLength={50}
          />
          <p className="text-xs text-gray-500 mt-1">{groupName.length}/50</p>
        </div>

        {/* 邀请好友 */}
        {canCreateGroup && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              邀请好友 (可选)
            </label>

            {/* 好友搜索 */}
            <div className="mb-3">
              <input
                type="text"
                placeholder="搜索好友..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* 已选择的好友数量 */}
            {selectedFriends.length > 0 && (
              <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  已选择 {selectedFriends.length} 位好友
                </p>
              </div>
            )}

            {/* 好友列表 */}
            <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
              {friendListLoading ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">加载好友列表中...</p>
                </div>
              ) : friendListError ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-red-600">{friendListError}</p>
                </div>
              ) : filteredFriends.length > 0 ? (
                <div className="p-2 space-y-1">
                  {filteredFriends.map(renderFriendItem)}
                </div>
              ) : searchQuery ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">未找到匹配的好友</p>
                </div>
              ) : friendList.length === 0 ? (
                <div className="p-4 text-center">
                  <p className="text-sm text-gray-500">暂无好友</p>
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* 错误信息 */}
        {createError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{createError}</p>
          </div>
        )}

        {/* 操作按钮 */}
        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 border border-gray-300 hover:border-gray-400 rounded-lg transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleCreateGroup}
            disabled={creating || !groupName.trim() || !canCreateGroup}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 rounded-lg transition-colors"
          >
            {creating ? '创建中...' : '创建群组'}
          </button>
        </div>
      </div>
    </BaseDialog>
  );
}
